import { c as _c } from "react/compiler-runtime";
import type { ThemeMode } from '@morpho-org/ui-v6/hooks';
import { type CSSProperties, type ForwardedRef, forwardRef, memo } from 'react';

// ----------------------------------------------------------------------------- STYLE

const _styles = {
  opacities: {
    full: 1.0,
    hidden: 0.2
  },
  colors: {
    full: '#FFF',
    half: '#777'
  },
  labels: {
    dashWidth: 0.5
  },
  transitions: {
    transition: 'opacity 170ms ease, transform 250ms cubic-bezier(0.33, 1, 0.68, 1), fill 170ms ease, stroke 170ms ease'
  }
};

// ----------------------------------------------------------------------------- GRAPHICS ELEMENTS
interface ArrowsProps {
  direction: 'up' | 'down';
  pathId: string;
  total?: number;
  duration?: number;
  fillColor?: string;
}
function Arrows({
  direction,
  pathId,
  total = 4,
  duration = total,
  fillColor = _styles.colors.full
}: ArrowsProps) {
  const isUp = direction === 'up';
  return Array.from({
    length: total
  }, (_, index) => <g key={index}>
      <path d="M12.8 1L7.2 5.8L1.5 1" stroke={fillColor} strokeOpacity="1" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" transform={`translate(0 ${isUp ? -7 : +7}) rotate(${isUp ? +90 : -90})`} />
      <animateMotion dur={`${duration}s`} begin={`${-index * (total / duration)}s`} rotate="auto" repeatCount="indefinite" keyPoints={isUp ? '1;0' : '0;1'} keyTimes="0;1">
        <mpath href={`#${pathId}`} />
      </animateMotion>
    </g>);
}
function VerticalCoinStroke(t0) {
  const $ = _c(9);
  const {
    transform,
    rotation,
    fill,
    stroke: t1
  } = t0;
  const stroke = t1 === undefined ? _styles.colors.half : t1;
  const t2 = `rotate(${rotation})`;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <path d="M24.8447 27.6773C4.78296 39.2603 4.67751 58.0401 24.6087 69.623C44.5398 81.206 76.9613 81.2061 97.0236 69.6245C117.087 58.041 117.192 39.2613 97.2609 27.6783C77.3279 16.0945 44.9064 16.0943 24.8447 27.6773Z" />;
    t4 = <path d="M9.74156 49.371C9.99444 56.9494 15.2546 63.2779 21.228 67.4562C30.6014 73.9972 42.1673 77.0255 53.4446 78.0074C63.0748 78.7897 72.8589 78.0674 82.2109 75.5962C95.3466 72.1474 111.925 63.2572 112.14 48.5033L112.16 59.2332C112.352 67.0228 106.327 75.3997 96.1295 80.8666C88.2586 85.1643 79.4308 87.4855 70.5561 88.498C55.6176 90.1354 38.7634 88.1385 25.4693 80.8289C19.1249 77.2904 13.0742 72.1824 10.6781 65.115C10.261 63.8482 9.96806 62.5377 9.83875 61.2102C9.62801 59.9484 9.79142 50.1518 9.72725 48.7958C9.72781 48.9875 9.73305 49.1791 9.74106 49.3696L9.74156 49.371Z" />;
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t2) {
    t5 = <g transform={t2}>{t3}{t4}</g>;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== fill || $[5] !== stroke || $[6] !== t5 || $[7] !== transform) {
    t6 = <g stroke={stroke} strokeWidth="0.75" strokeMiterlimit="10" transform={transform} fill={fill}>{t5}</g>;
    $[4] = fill;
    $[5] = stroke;
    $[6] = t5;
    $[7] = transform;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
interface CollateralCoinLogoProps {
  selected: boolean;
  fillColor: string;
}
function CollateralCoinLogo(t0) {
  const $ = _c(6);
  const {
    selected,
    fillColor
  } = t0;
  const opacity = selected ? _styles.opacities.full : _styles.opacities.hidden;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <g fillOpacity="0.6"><path d="M150.123 205.607C153.298 209.463 152.632 217.274 148.389 224.466C145.926 228.732 142.773 231.978 139.477 233.878C139.477 233.878 139.559 233.656 149.848 205.29L150.126 205.607H150.123Z" /><path d="M128.969 217.185L139.425 218.102C139.441 223.31 139.457 228.99 139.47 233.882C139.47 233.882 139.388 233.753 128.969 217.185Z" /><path d="M139.415 214.632L130.336 214.027L139.384 202.799L139.415 214.632Z" /><path d="M148.46 203.561L139.381 202.797L139.352 192.856L148.46 203.561Z" /><path d="M150.123 205.607C153.298 209.463 152.632 217.274 148.389 224.466C145.926 228.732 142.773 231.978 139.477 233.878C139.477 233.878 139.559 233.656 149.848 205.29L150.126 205.607H150.123Z" /><path d="M128.969 217.185L139.425 218.102C139.441 223.31 139.457 228.99 139.47 233.882C139.47 233.882 139.388 233.753 128.969 217.185Z" /><path d="M139.415 214.632L130.336 214.027L139.384 202.799L139.415 214.632Z" /><path d="M148.46 203.561L139.381 202.797L139.352 192.856L148.46 203.561Z" /></g>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <g fillOpacity="1"><path d="M139.384 202.797L130.336 214.026L139.355 192.856L139.384 202.797Z" /><path d="M128.694 217.974L128.966 217.342L139.465 233.88C136.169 235.783 132.87 236.264 130.528 234.774C126.259 232.498 125.551 225.478 128.694 217.974Z" /><path d="M139.384 202.797L130.336 214.026L139.355 192.856L139.384 202.797Z" /><path d="M128.694 217.974L128.966 217.342L139.465 233.88C136.169 235.783 132.87 236.264 130.528 234.774C126.259 232.498 125.551 225.478 128.694 217.974Z" /></g>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <g fillOpacity="0.2"><path d="M139.422 218.102L149.839 205.135L139.467 233.882C139.454 228.992 139.438 223.309 139.422 218.102Z" /><path d="M139.415 214.631L139.383 202.798L148.462 203.562L139.415 214.631Z" /><path d="M139.422 218.102L149.839 205.135L139.467 233.882C139.454 228.992 139.438 223.309 139.422 218.102Z" /><path d="M139.415 214.631L139.383 202.798L148.462 203.562L139.415 214.631Z" /></g>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== fillColor || $[4] !== opacity) {
    t4 = <g fill={fillColor} opacity={opacity} style={_styles.transitions}>{t1}{t2}{t3}</g>;
    $[3] = fillColor;
    $[4] = opacity;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
interface LoanCoinLogoProps {
  selected: boolean;
  fillColor: string;
}
function LoanCoinLogo(t0) {
  const $ = _c(8);
  const {
    selected,
    fillColor
  } = t0;
  const opacity = selected ? _styles.opacities.full : _styles.opacities.hidden;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M271.02 201.202C271.557 201.636 271.878 202.193 271.877 202.687L271.868 205.65C274.338 207.323 276.266 210.538 276.688 213.502V213.626C276.687 214.12 276.363 214.304 275.933 214.055L274.215 213.063C273.892 212.877 273.571 212.445 273.464 212.012C272.933 209.851 271.861 208.489 269.927 207.373C267.778 206.132 266.7 206.623 266.696 208.351C266.691 210.08 267.332 211.439 270.554 213.794C275.065 217.016 277.317 219.8 277.304 224.121C277.295 227.454 275.139 228.806 271.914 227.562L271.906 230.525C271.797 231.08 271.473 231.265 271.043 231.016L269.432 230.086C268.895 229.652 268.574 229.096 268.575 228.602L268.584 225.639C265.04 222.975 263.327 219.761 262.906 216.426V216.303C262.908 215.809 263.232 215.624 263.661 215.873L265.487 216.927C265.81 217.113 266.131 217.546 266.237 218.102C266.554 220.139 267.517 222.056 270.311 223.669C272.352 224.847 273.86 224.358 273.865 222.382C273.871 220.407 272.906 219.108 269.9 216.754C265.389 213.532 263.245 210.686 263.257 206.612C263.265 203.526 265.313 202.236 268.538 203.603L268.546 200.763C268.655 200.208 268.979 200.024 269.409 200.271L271.02 201.202L271.02 201.202Z" />;
    t2 = <path d="M288.721 219.709C291.911 233.297 286.289 242.54 276.072 240.351C275.964 240.413 275.749 240.289 275.642 240.226C275.212 239.855 274.999 239.361 275 238.867L275.005 237.139C275.007 236.522 275.222 236.275 275.652 236.276C280.063 236.968 283.619 235.064 285.245 230.81C288.388 223.103 284.121 209.882 275.751 201.587C275.43 201.153 275.109 200.474 275.11 199.98L275.115 198.252C275.116 198.005 275.116 197.881 275.224 197.82C275.303 197.503 275.555 197.42 275.853 197.592C275.961 197.654 276.077 197.752 276.192 197.884C282.094 203.518 286.806 211.555 288.72 219.709H288.721Z" />;
    t3 = <path d="M264.808 191.187C265.238 191.558 265.451 192.053 265.45 192.547L265.445 194.275C265.443 194.892 265.227 195.139 264.797 195.137C260.387 194.446 256.831 196.349 255.205 200.604C252.062 208.311 256.329 221.532 264.699 229.827C265.02 230.26 265.341 230.94 265.34 231.31L265.335 233.038C265.335 233.285 265.334 233.409 265.227 233.47C265.147 233.786 264.895 233.87 264.597 233.699C264.489 233.636 264.374 233.538 264.258 233.406C258.248 227.71 253.643 219.734 251.729 211.704C248.539 198.116 254.161 188.873 264.378 191.061C264.486 191.001 264.7 191.125 264.808 191.186V191.187Z" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== fillColor) {
    t4 = <g fill={fillColor} fillOpacity="0.9" style={_styles.transitions}>{t1}{t2}{t3}</g>;
    $[3] = fillColor;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== opacity || $[6] !== t4) {
    t5 = <g opacity={opacity} style={_styles.transitions}>{t4}</g>;
    $[5] = opacity;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

// ----------------------------------------------------------------------------- GRAPHICS ROOT

interface HowItWorksBorrowGraphicsProps {
  style?: CSSProperties;
  className?: string;
  base: string;
  selectedPart?: number;
  backgroundFillColor?: string;
  textFillColor: string;
  pathsFillColor: string;
  themeMode: ThemeMode;
}
function HowItWorksBorrowGraphics(t0, ref) {
  const $ = _c(144);
  const {
    style,
    className,
    base,
    selectedPart,
    backgroundFillColor,
    textFillColor,
    pathsFillColor,
    themeMode
  } = t0;
  const t1 = selectedPart === 0 ? _styles.opacities.full : _styles.opacities.hidden;
  let t2;
  if ($[0] !== textFillColor) {
    t2 = <path d="M0.93348 212.604C0.93348 211.577 1.11981 210.699 1.49248 209.971C1.86515 209.243 2.39598 208.691 3.08498 208.314C3.77831 207.933 4.59298 207.742 5.52898 207.742C6.39998 207.742 7.13665 207.898 7.73898 208.21C8.34565 208.518 8.80281 208.91 9.11048 209.386C9.41815 209.859 9.58931 210.342 9.62398 210.836V211.031H8.51248C8.48215 210.676 8.36081 210.327 8.14848 209.984C7.94048 209.638 7.61981 209.35 7.18648 209.12C6.75315 208.886 6.20715 208.769 5.54848 208.769C4.83781 208.769 4.22681 208.918 3.71548 209.217C3.20848 209.516 2.81848 209.954 2.54548 210.53C2.27681 211.102 2.14248 211.794 2.14248 212.604C2.14248 213.414 2.27681 214.108 2.54548 214.684C2.81848 215.256 3.20848 215.691 3.71548 215.99C4.22681 216.289 4.83781 216.439 5.54848 216.439C6.20715 216.439 6.75315 216.322 7.18648 216.088C7.62415 215.85 7.94915 215.555 8.16148 215.204C8.37381 214.853 8.49948 214.489 8.53848 214.112H9.64998V214.307C9.60665 214.814 9.42898 215.308 9.11698 215.789C8.80931 216.27 8.35215 216.671 7.74548 216.991C7.14315 217.308 6.40431 217.466 5.52898 217.466C4.59298 217.466 3.77831 217.277 3.08498 216.9C2.39598 216.519 1.86515 215.964 1.49248 215.236C1.11981 214.508 0.93348 213.631 0.93348 212.604ZM10.7398 213.975C10.7398 213.243 10.8785 212.617 11.1558 212.097C11.4375 211.577 11.8318 211.183 12.3388 210.914C12.8458 210.641 13.4373 210.504 14.1133 210.504C14.785 210.504 15.3743 210.641 15.8813 210.914C16.3927 211.183 16.787 211.577 17.0643 212.097C17.346 212.617 17.4868 213.243 17.4868 213.975C17.4868 214.703 17.346 215.33 17.0643 215.854C16.787 216.374 16.3927 216.768 15.8813 217.037C15.3743 217.306 14.785 217.44 14.1133 217.44C13.4373 217.44 12.8458 217.306 12.3388 217.037C11.8318 216.768 11.4375 216.374 11.1558 215.854C10.8785 215.33 10.7398 214.703 10.7398 213.975ZM11.8968 213.975C11.8968 214.513 11.9878 214.972 12.1698 215.353C12.3562 215.73 12.614 216.016 12.9433 216.211C13.277 216.406 13.667 216.504 14.1133 216.504C14.5553 216.504 14.941 216.406 15.2703 216.211C15.604 216.016 15.8618 215.73 16.0438 215.353C16.2302 214.972 16.3233 214.513 16.3233 213.975C16.3233 213.438 16.2323 212.981 16.0503 212.604C15.8683 212.223 15.6105 211.934 15.2768 211.739C14.9432 211.544 14.5553 211.447 14.1133 211.447C13.667 211.447 13.277 211.544 12.9433 211.739C12.614 211.934 12.3562 212.223 12.1698 212.604C11.9878 212.981 11.8968 213.438 11.8968 213.975ZM20.1134 207.924V217.284H18.9694V207.924H20.1134ZM23.1475 207.924V217.284H22.0035V207.924H23.1475ZM24.9597 212.5C25.003 212.093 25.146 211.739 25.3887 211.44C25.6357 211.137 25.9694 210.905 26.3897 210.745C26.81 210.585 27.3019 210.504 27.8652 210.504C28.7362 210.504 29.4035 210.706 29.8672 211.109C30.3352 211.508 30.5692 212.112 30.5692 212.922V217.284H29.5032V216.374H29.3992C29.2389 216.673 28.9659 216.924 28.5802 217.128C28.1989 217.327 27.6962 217.427 27.0722 217.427C26.5955 217.427 26.173 217.349 25.8047 217.193C25.4364 217.037 25.1504 216.812 24.9467 216.517C24.743 216.222 24.6412 215.873 24.6412 215.47C24.6412 214.851 24.8492 214.376 25.2652 214.047C25.6812 213.713 26.251 213.501 26.9747 213.41L29.4642 213.098V212.857C29.4642 212.35 29.3299 211.984 29.0612 211.759C28.7925 211.529 28.3852 211.414 27.8392 211.414C27.2932 211.414 26.8664 211.521 26.5587 211.733C26.251 211.945 26.0755 212.266 26.0322 212.695H24.9597V212.5ZM25.7787 215.418C25.7787 215.761 25.9109 216.027 26.1752 216.218C26.4439 216.409 26.8057 216.504 27.2607 216.504C27.6767 216.504 28.0494 216.428 28.3787 216.276C28.7124 216.12 28.9767 215.884 29.1717 215.568C29.3667 215.252 29.4642 214.859 29.4642 214.391V213.982L27.2022 214.281C26.7429 214.342 26.3897 214.463 26.1427 214.645C25.9 214.827 25.7787 215.085 25.7787 215.418ZM33.0621 210.66V208.84H34.2061V210.66H36.1886V211.59H34.2061V216.192L34.3101 216.296H36.1691V217.284H34.7651C34.2147 217.284 33.7922 217.147 33.4976 216.874C33.2072 216.601 33.0621 216.198 33.0621 215.665V211.59H31.4956V210.66H33.0621ZM37.0572 213.969C37.0572 213.232 37.1872 212.604 37.4472 212.084C37.7072 211.564 38.0777 211.172 38.5587 210.907C39.0397 210.639 39.6096 210.504 40.2682 210.504C40.9182 210.504 41.4707 210.634 41.9257 210.894C42.3851 211.15 42.7317 211.516 42.9657 211.993C43.2041 212.47 43.3232 213.033 43.3232 213.683V214.255H38.1622C38.1709 214.714 38.2597 215.115 38.4287 215.457C38.6021 215.795 38.8447 216.058 39.1567 216.244C39.4731 216.426 39.8436 216.517 40.2682 216.517C40.6539 216.517 40.9811 216.454 41.2497 216.328C41.5227 216.198 41.7329 216.036 41.8802 215.841C42.0276 215.642 42.1207 215.431 42.1597 215.21H43.1867V215.405C43.1304 215.717 42.9939 216.029 42.7772 216.341C42.5649 216.649 42.2486 216.909 41.8282 217.121C41.4122 217.334 40.8966 217.44 40.2812 217.44C39.6226 217.44 39.0506 217.308 38.5652 217.043C38.0842 216.775 37.7116 216.38 37.4472 215.86C37.1872 215.34 37.0572 214.71 37.0572 213.969ZM42.2312 213.351C42.2269 212.97 42.1489 212.634 41.9972 212.344C41.8456 212.054 41.6224 211.828 41.3277 211.668C41.0374 211.503 40.6821 211.421 40.2617 211.421C39.8501 211.421 39.4926 211.503 39.1892 211.668C38.8902 211.833 38.6541 212.06 38.4807 212.35C38.3117 212.641 38.2121 212.974 38.1817 213.351H42.2312ZM45.922 210.66V211.453H46.026C46.1517 211.193 46.3402 210.992 46.5915 210.849C46.8428 210.706 47.2003 210.634 47.664 210.634H48.587V211.622H47.69C47.079 211.622 46.637 211.789 46.364 212.123C46.0953 212.452 45.961 212.961 45.961 213.65V217.284H44.817V210.66H45.922ZM49.4871 212.5C49.5304 212.093 49.6734 211.739 49.9161 211.44C50.1631 211.137 50.4967 210.905 50.9171 210.745C51.3374 210.585 51.8292 210.504 52.3926 210.504C53.2636 210.504 53.9309 210.706 54.3946 211.109C54.8626 211.508 55.0966 212.112 55.0966 212.922V217.284H54.0306V216.374H53.9266C53.7662 216.673 53.4932 216.924 53.1076 217.128C52.7262 217.327 52.2236 217.427 51.5996 217.427C51.1229 217.427 50.7004 217.349 50.3321 217.193C49.9637 217.037 49.6777 216.812 49.4741 216.517C49.2704 216.222 49.1686 215.873 49.1686 215.47C49.1686 214.851 49.3766 214.376 49.7926 214.047C50.2086 213.713 50.7784 213.501 51.5021 213.41L53.9916 213.098V212.857C53.9916 212.35 53.8572 211.984 53.5886 211.759C53.3199 211.529 52.9126 211.414 52.3666 211.414C51.8206 211.414 51.3937 211.521 51.0861 211.733C50.7784 211.945 50.6029 212.266 50.5596 212.695H49.4871V212.5ZM50.3061 215.418C50.3061 215.761 50.4382 216.027 50.7026 216.218C50.9712 216.409 51.3331 216.504 51.7881 216.504C52.2041 216.504 52.5767 216.428 52.9061 216.276C53.2397 216.12 53.5041 215.884 53.6991 215.568C53.8941 215.252 53.9916 214.859 53.9916 214.391V213.982L51.7296 214.281C51.2702 214.342 50.9171 214.463 50.6701 214.645C50.4274 214.827 50.3061 215.085 50.3061 215.418ZM58.0723 207.924V217.284H56.9283V207.924H58.0723Z" fill={textFillColor} />;
    $[0] = textFillColor;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== pathsFillColor) {
    t3 = <path d="M110.931 212.785L69.9941 212.782" stroke={pathsFillColor} strokeWidth={_styles.labels.dashWidth} strokeLinecap="round" />;
    $[2] = pathsFillColor;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <g opacity={t1} style={_styles.transitions}>{t2}{t3}</g>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = selectedPart === 0 ? _styles.opacities.full : _styles.opacities.hidden;
  let t6;
  if ($[8] !== pathsFillColor) {
    t6 = <path d="M178.125 95.8132L156.42 95.8132C141.682 95.8132 129.734 107.761 129.734 122.499L129.734 312.156C129.734 326.894 141.682 338.841 156.42 338.841L178.125 338.841" stroke={pathsFillColor} strokeOpacity="0.9" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="0.09 3.6" id="collateral-path" />;
    $[8] = pathsFillColor;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <g opacity={t5} style={_styles.transitions}>{t6}</g>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = selectedPart === 0 ? 1 : 0;
  let t9;
  if ($[13] !== pathsFillColor) {
    t9 = <Arrows direction="down" pathId="collateral-path" total={4} fillColor={pathsFillColor} />;
    $[13] = pathsFillColor;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <g opacity={t8} style={_styles.transitions}>{t9}</g>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== backgroundFillColor) {
    t11 = <VerticalCoinStroke transform="translate(196.5 180.4) scale(0.86) rotate(20.6)" fill={backgroundFillColor} rotation={90} />;
    $[18] = backgroundFillColor;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = `${base}/vertical-color-coin.png`;
  const t13 = selectedPart === 0 ? 1 : 0;
  let t14;
  if ($[20] !== t12 || $[21] !== t13) {
    t14 = <image x="101.398" y="168.753" width="65.9989" height="88.8144" href={t12} opacity={t13} style={_styles.transitions} />;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = selectedPart === 0;
  const t16 = selectedPart === 0 ? _styles.colors.full : pathsFillColor;
  let t17;
  if ($[23] !== t15 || $[24] !== t16) {
    t17 = <CollateralCoinLogo selected={t15} fillColor={t16} />;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t14 || $[29] !== t17 || $[30] !== t4 || $[31] !== t7) {
    t18 = <g>{t4}{t7}{t10}{t11}{t14}{t17}</g>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t14;
    $[29] = t17;
    $[30] = t4;
    $[31] = t7;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = selectedPart % 2 === 1 ? _styles.opacities.full : _styles.opacities.hidden;
  let t20;
  if ($[33] !== textFillColor) {
    t20 = <path d="M353.817 208.276V216.589H359.017V217.636H352.621V208.276H353.817ZM359.826 214.327C359.826 213.595 359.965 212.969 360.242 212.449C360.524 211.929 360.918 211.534 361.425 211.266C361.932 210.993 362.524 210.856 363.2 210.856C363.871 210.856 364.461 210.993 364.968 211.266C365.479 211.534 365.873 211.929 366.151 212.449C366.432 212.969 366.573 213.595 366.573 214.327C366.573 215.055 366.432 215.681 366.151 216.206C365.873 216.726 365.479 217.12 364.968 217.389C364.461 217.657 363.871 217.792 363.2 217.792C362.524 217.792 361.932 217.657 361.425 217.389C360.918 217.12 360.524 216.726 360.242 216.206C359.965 215.681 359.826 215.055 359.826 214.327ZM360.983 214.327C360.983 214.865 361.074 215.324 361.256 215.705C361.443 216.082 361.7 216.368 362.03 216.563C362.363 216.758 362.753 216.856 363.2 216.856C363.642 216.856 364.027 216.758 364.357 216.563C364.69 216.368 364.948 216.082 365.13 215.705C365.317 215.324 365.41 214.865 365.41 214.327C365.41 213.79 365.319 213.333 365.137 212.956C364.955 212.574 364.697 212.286 364.363 212.091C364.03 211.896 363.642 211.799 363.2 211.799C362.753 211.799 362.363 211.896 362.03 212.091C361.7 212.286 361.443 212.574 361.256 212.956C361.074 213.333 360.983 213.79 360.983 214.327ZM367.952 212.852C367.996 212.444 368.139 212.091 368.381 211.792C368.628 211.489 368.962 211.257 369.382 211.097C369.803 210.936 370.295 210.856 370.858 210.856C371.729 210.856 372.396 211.058 372.86 211.461C373.328 211.859 373.562 212.464 373.562 213.274V217.636H372.496V216.726H372.392C372.232 217.025 371.959 217.276 371.573 217.48C371.192 217.679 370.689 217.779 370.065 217.779C369.588 217.779 369.166 217.701 368.797 217.545C368.429 217.389 368.143 217.163 367.939 216.869C367.736 216.574 367.634 216.225 367.634 215.822C367.634 215.203 367.842 214.728 368.258 214.399C368.674 214.065 369.244 213.853 369.967 213.762L372.457 213.45V213.209C372.457 212.702 372.323 212.336 372.054 212.111C371.785 211.881 371.378 211.766 370.832 211.766C370.286 211.766 369.859 211.872 369.551 212.085C369.244 212.297 369.068 212.618 369.025 213.047H367.952V212.852ZM368.771 215.77C368.771 216.113 368.904 216.379 369.168 216.57C369.437 216.76 369.798 216.856 370.253 216.856C370.669 216.856 371.042 216.78 371.371 216.628C371.705 216.472 371.969 216.236 372.164 215.92C372.359 215.603 372.457 215.211 372.457 214.743V214.334L370.195 214.633C369.736 214.693 369.382 214.815 369.135 214.997C368.893 215.179 368.771 215.437 368.771 215.77ZM376.505 211.012V211.864H376.609C377.047 211.196 377.786 210.863 378.826 210.863C379.614 210.863 380.228 211.064 380.665 211.467C381.107 211.87 381.328 212.468 381.328 213.261V217.636H380.184V213.372C380.184 212.336 379.645 211.818 378.566 211.818C378.176 211.818 377.829 211.896 377.526 212.052C377.222 212.208 376.982 212.444 376.804 212.761C376.627 213.077 376.538 213.469 376.538 213.937V217.636H375.394V211.012H376.505Z" fill={textFillColor} />;
    $[33] = textFillColor;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== pathsFillColor) {
    t21 = <path d="M340.577 213.136H264.969" stroke={pathsFillColor} strokeWidth={_styles.labels.dashWidth} strokeLinecap="round" />;
    $[35] = pathsFillColor;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t19 || $[38] !== t20 || $[39] !== t21) {
    t22 = <g opacity={t19} style={_styles.transitions}>{t20}{t21}</g>;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  const t23 = selectedPart % 2 === 1 ? _styles.opacities.full : _styles.opacities.hidden;
  let t24;
  if ($[41] !== pathsFillColor) {
    t24 = <path d="M228.828 338.842L250.533 338.842C265.271 338.842 277.219 326.894 277.219 312.157L277.219 122.499C277.219 107.761 265.271 95.814 250.533 95.814L228.828 95.814" stroke={pathsFillColor} strokeOpacity="0.9" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="0.09 3.6" id="load-path" />;
    $[41] = pathsFillColor;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t23 || $[44] !== t24) {
    t25 = <g opacity={t23} style={_styles.transitions}>{t24}</g>;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  const t26 = selectedPart === 1 ? 1 : 0;
  let t27;
  if ($[46] !== pathsFillColor) {
    t27 = <Arrows direction="down" pathId="load-path" fillColor={pathsFillColor} />;
    $[46] = pathsFillColor;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t26 || $[49] !== t27) {
    t28 = <g opacity={t26} style={_styles.transitions}>{t27}</g>;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  const t29 = selectedPart === 3 ? 1 : 0;
  let t30;
  if ($[51] !== pathsFillColor) {
    t30 = <Arrows direction="up" pathId="load-path" fillColor={pathsFillColor} />;
    $[51] = pathsFillColor;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t29 || $[54] !== t30) {
    t31 = <g opacity={t29} style={_styles.transitions}>{t30}</g>;
    $[53] = t29;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== backgroundFillColor) {
    t32 = <VerticalCoinStroke transform="translate(248.2 278.3) scale(0.86) rotate(-20)" rotation={-90} fill={backgroundFillColor} />;
    $[56] = backgroundFillColor;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  const t33 = `${base}/vertical-color-coin.png`;
  const t34 = selectedPart % 2 === 1 ? 1 : 0;
  let t35;
  if ($[58] !== t33 || $[59] !== t34) {
    t35 = <image width="65.9989" height="88.8144" transform="matrix(-1 0 0 1 306.789 168.753)" href={t33} opacity={t34} style={_styles.transitions} />;
    $[58] = t33;
    $[59] = t34;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  const t36 = selectedPart % 2 === 1;
  const t37 = selectedPart % 2 === 1 ? _styles.colors.full : pathsFillColor;
  let t38;
  if ($[61] !== t36 || $[62] !== t37) {
    t38 = <LoanCoinLogo selected={t36} fillColor={t37} />;
    $[61] = t36;
    $[62] = t37;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  const t39 = selectedPart === 3 ? 1 : 0;
  const t40 = selectedPart === 3 ? "" : "translate(-6, 2)";
  const t41 = `${base}/yield-${themeMode}.png`;
  let t42;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      filter: "brightness(130%) contrast(0.9)"
    };
    $[64] = t42;
  } else {
    t42 = $[64];
  }
  let t43;
  if ($[65] !== t41) {
    t43 = <image width="50" height="45" x="270" y="180" href={t41} style={t42} />;
    $[65] = t41;
    $[66] = t43;
  } else {
    t43 = $[66];
  }
  let t44;
  if ($[67] !== t39 || $[68] !== t40 || $[69] !== t43) {
    t44 = <g style={_styles.transitions} opacity={t39} transform={t40}>{t43}</g>;
    $[67] = t39;
    $[68] = t40;
    $[69] = t43;
    $[70] = t44;
  } else {
    t44 = $[70];
  }
  let t45;
  if ($[71] !== t22 || $[72] !== t25 || $[73] !== t28 || $[74] !== t31 || $[75] !== t32 || $[76] !== t35 || $[77] !== t38 || $[78] !== t44) {
    t45 = <g>{t22}{t25}{t28}{t31}{t32}{t35}{t38}{t44}</g>;
    $[71] = t22;
    $[72] = t25;
    $[73] = t28;
    $[74] = t31;
    $[75] = t32;
    $[76] = t35;
    $[77] = t38;
    $[78] = t44;
    $[79] = t45;
  } else {
    t45 = $[79];
  }
  let t46;
  if ($[80] !== textFillColor) {
    t46 = <path d="M5.53206 69.8597C6.0954 69.8597 6.5959 69.9486 7.03356 70.1262C7.47123 70.3039 7.81356 70.5704 8.06056 70.9257C8.30756 71.2767 8.43106 71.7079 8.43106 72.2192C8.43106 72.6786 8.34873 73.0577 8.18406 73.3567C8.0194 73.6557 7.82223 73.8811 7.59256 74.0327C7.3629 74.1801 7.13323 74.2797 6.90356 74.3317V74.4357C7.3369 74.5354 7.72906 74.7542 8.08006 75.0922C8.43106 75.4302 8.60656 75.9567 8.60656 76.6717C8.60656 77.2264 8.4744 77.6966 8.21006 78.0822C7.95006 78.4636 7.59256 78.7496 7.13756 78.9402C6.68256 79.1266 6.16256 79.2197 5.57756 79.2197H1.41106V69.8597H5.53206ZM5.44756 78.2122C6.05856 78.2122 6.54173 78.0822 6.89706 77.8222C7.25673 77.5579 7.43656 77.1462 7.43656 76.5872C7.43656 76.0282 7.2589 75.6187 6.90356 75.3587C6.54823 75.0987 6.0629 74.9687 5.44756 74.9687H2.58756V78.2122H5.44756ZM5.38256 73.9612C5.96323 73.9612 6.42473 73.8377 6.76706 73.5907C7.11373 73.3437 7.28706 72.9516 7.28706 72.4142C7.28706 71.8769 7.11373 71.4847 6.76706 71.2377C6.42473 70.9907 5.96323 70.8672 5.38256 70.8672H2.58756V73.9612H5.38256ZM9.70811 75.9112C9.70811 75.1789 9.84678 74.5527 10.1241 74.0327C10.4058 73.5127 10.8001 73.1184 11.3071 72.8497C11.8141 72.5767 12.4056 72.4402 13.0816 72.4402C13.7533 72.4402 14.3426 72.5767 14.8496 72.8497C15.3609 73.1184 15.7553 73.5127 16.0326 74.0327C16.3143 74.5527 16.4551 75.1789 16.4551 75.9112C16.4551 76.6392 16.3143 77.2654 16.0326 77.7897C15.7553 78.3097 15.3609 78.7041 14.8496 78.9727C14.3426 79.2414 13.7533 79.3757 13.0816 79.3757C12.4056 79.3757 11.8141 79.2414 11.3071 78.9727C10.8001 78.7041 10.4058 78.3097 10.1241 77.7897C9.84678 77.2654 9.70811 76.6392 9.70811 75.9112ZM10.8651 75.9112C10.8651 76.4486 10.9561 76.9079 11.1381 77.2892C11.3244 77.6662 11.5823 77.9522 11.9116 78.1472C12.2453 78.3422 12.6353 78.4397 13.0816 78.4397C13.5236 78.4397 13.9093 78.3422 14.2386 78.1472C14.5723 77.9522 14.8301 77.6662 15.0121 77.2892C15.1984 76.9079 15.2916 76.4486 15.2916 75.9112C15.2916 75.3739 15.2006 74.9167 15.0186 74.5397C14.8366 74.1584 14.5788 73.8702 14.2451 73.6752C13.9114 73.4802 13.5236 73.3827 13.0816 73.3827C12.6353 73.3827 12.2453 73.4802 11.9116 73.6752C11.5823 73.8702 11.3244 74.1584 11.1381 74.5397C10.9561 74.9167 10.8651 75.3739 10.8651 75.9112ZM19.0426 72.5962V73.3892H19.1466C19.2723 73.1292 19.4608 72.9277 19.7121 72.7847C19.9635 72.6417 20.321 72.5702 20.7846 72.5702H21.7076V73.5582H20.8106C20.1996 73.5582 19.7576 73.7251 19.4846 74.0587C19.216 74.3881 19.0816 74.8972 19.0816 75.5862V79.2197H17.9376V72.5962H19.0426ZM24.0827 72.5962V73.3892H24.1867C24.3123 73.1292 24.5008 72.9277 24.7522 72.7847C25.0035 72.6417 25.361 72.5702 25.8247 72.5702H26.7477V73.5582H25.8507C25.2397 73.5582 24.7977 73.7251 24.5247 74.0587C24.256 74.3881 24.1217 74.8972 24.1217 75.5862V79.2197H22.9777V72.5962H24.0827ZM27.3673 75.9112C27.3673 75.1789 27.506 74.5527 27.7833 74.0327C28.065 73.5127 28.4593 73.1184 28.9663 72.8497C29.4733 72.5767 30.0648 72.4402 30.7408 72.4402C31.4125 72.4402 32.0018 72.5767 32.5088 72.8497C33.0201 73.1184 33.4145 73.5127 33.6918 74.0327C33.9735 74.5527 34.1143 75.1789 34.1143 75.9112C34.1143 76.6392 33.9735 77.2654 33.6918 77.7897C33.4145 78.3097 33.0201 78.7041 32.5088 78.9727C32.0018 79.2414 31.4125 79.3757 30.7408 79.3757C30.0648 79.3757 29.4733 79.2414 28.9663 78.9727C28.4593 78.7041 28.065 78.3097 27.7833 77.7897C27.506 77.2654 27.3673 76.6392 27.3673 75.9112ZM28.5243 75.9112C28.5243 76.4486 28.6153 76.9079 28.7973 77.2892C28.9836 77.6662 29.2415 77.9522 29.5708 78.1472C29.9045 78.3422 30.2945 78.4397 30.7408 78.4397C31.1828 78.4397 31.5685 78.3422 31.8978 78.1472C32.2315 77.9522 32.4893 77.6662 32.6713 77.2892C32.8576 76.9079 32.9508 76.4486 32.9508 75.9112C32.9508 75.3739 32.8598 74.9167 32.6778 74.5397C32.4958 74.1584 32.238 73.8702 31.9043 73.6752C31.5706 73.4802 31.1828 73.3827 30.7408 73.3827C30.2945 73.3827 29.9045 73.4802 29.5708 73.6752C29.2415 73.8702 28.9836 74.1584 28.7973 74.5397C28.6153 74.9167 28.5243 75.3739 28.5243 75.9112ZM35.949 72.5962L37.2945 77.9132H37.405L39.017 72.5962H40.2845L41.903 77.9132H42.007L43.346 72.5962H44.4055V72.7912L42.67 79.2197H41.2985L39.6865 73.9222H39.5825L37.9705 79.2197H36.5925L34.857 72.7912V72.5962H35.949ZM45.1661 75.9047C45.1661 75.1681 45.2961 74.5397 45.5561 74.0197C45.8161 73.4997 46.1866 73.1076 46.6676 72.8432C47.1486 72.5746 47.7185 72.4402 48.3771 72.4402C49.0271 72.4402 49.5796 72.5702 50.0346 72.8302C50.494 73.0859 50.8406 73.4521 51.0746 73.9287C51.313 74.4054 51.4321 74.9687 51.4321 75.6187V76.1907H46.2711C46.2798 76.6501 46.3686 77.0509 46.5376 77.3932C46.711 77.7312 46.9536 77.9934 47.2656 78.1797C47.582 78.3617 47.9525 78.4527 48.3771 78.4527C48.7628 78.4527 49.09 78.3899 49.3586 78.2642C49.6316 78.1342 49.8418 77.9717 49.9891 77.7767C50.1365 77.5774 50.2296 77.3672 50.2686 77.1462H51.2956V77.3412C51.2393 77.6532 51.1028 77.9652 50.8861 78.2772C50.6738 78.5849 50.3575 78.8449 49.9371 79.0572C49.5211 79.2696 49.0055 79.3757 48.3901 79.3757C47.7315 79.3757 47.1595 79.2436 46.6741 78.9792C46.1931 78.7106 45.8205 78.3162 45.5561 77.7962C45.2961 77.2762 45.1661 76.6457 45.1661 75.9047ZM50.3401 75.2872C50.3358 74.9059 50.2578 74.5701 50.1061 74.2797C49.9545 73.9894 49.7313 73.7641 49.4366 73.6037C49.1463 73.4391 48.791 73.3567 48.3706 73.3567C47.959 73.3567 47.6015 73.4391 47.2981 73.6037C46.9991 73.7684 46.763 73.9959 46.5896 74.2862C46.4206 74.5766 46.321 74.9102 46.2906 75.2872H50.3401ZM54.0309 72.5962V73.3892H54.1349C54.2606 73.1292 54.4491 72.9277 54.7004 72.7847C54.9517 72.6417 55.3092 72.5702 55.7729 72.5702H56.6959V73.5582H55.7989C55.1879 73.5582 54.7459 73.7251 54.4729 74.0587C54.2042 74.3881 54.0699 74.8972 54.0699 75.5862V79.2197H52.9259V72.5962H54.0309Z" fill={textFillColor} />;
    $[80] = textFillColor;
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] !== pathsFillColor) {
    t47 = <path d="M202.828 74.7213L67.9941 74.7183" stroke={pathsFillColor} strokeWidth={_styles.labels.dashWidth} strokeLinecap="round" />;
    $[82] = pathsFillColor;
    $[83] = t47;
  } else {
    t47 = $[83];
  }
  const t48 = `${base}/person.png`;
  let t49;
  if ($[84] !== t48) {
    t49 = <image x="161.445" y="0.66748" width="83.1511" height="122.947" href={t48} />;
    $[84] = t48;
    $[85] = t49;
  } else {
    t49 = $[85];
  }
  const t50 = selectedPart === 2 ? "" : "translate(5, 3)";
  const t51 = selectedPart === 2 ? 1 : 0;
  const t52 = `${base}/screen.png`;
  let t53;
  if ($[86] !== t52) {
    t53 = <image x="220.031" y="20.6509" width="93.3986" height="124.531" href={t52} />;
    $[86] = t52;
    $[87] = t53;
  } else {
    t53 = $[87];
  }
  const t54 = selectedPart === 2 ? 1 : 0;
  let t55;
  let t56;
  let t57;
  let t58;
  let t59;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <line x1="0.667133" y1="-0.667133" x2="99.7334" y2="-0.667133" transform="matrix(-0.868243 0.496139 0.496139 0.868243 313.43 33.1038)" />;
    t56 = <line x1="0.667133" y1="-0.667133" x2="99.7334" y2="-0.667133" transform="matrix(-0.868243 0.496139 0.496139 0.868243 313.43 45.5566)" />;
    t57 = <line x1="0.667133" y1="-0.667133" x2="99.7334" y2="-0.667133" transform="matrix(-0.868243 0.496139 0.496139 0.868243 313.43 58.0107)" />;
    t58 = <line x1="0.667133" y1="-0.667133" x2="99.7334" y2="-0.667133" transform="matrix(-0.868243 0.496139 0.496139 0.868243 313.43 70.4636)" />;
    t59 = <line x1="0.667133" y1="-0.667133" x2="99.7334" y2="-0.667133" transform="matrix(-0.868243 0.496139 0.496139 0.868243 313.43 82.9153)" />;
    $[88] = t55;
    $[89] = t56;
    $[90] = t57;
    $[91] = t58;
    $[92] = t59;
  } else {
    t55 = $[88];
    t56 = $[89];
    t57 = $[90];
    t58 = $[91];
    t59 = $[92];
  }
  let t60;
  if ($[93] !== pathsFillColor) {
    t60 = <g opacity="0.5" stroke={pathsFillColor} strokeWidth="1.33" strokeLinecap="round" strokeDasharray="0.13 4">{t55}{t56}{t57}{t58}{t59}</g>;
    $[93] = pathsFillColor;
    $[94] = t60;
  } else {
    t60 = $[94];
  }
  let t61;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <g stroke={_styles.colors.full} strokeWidth="1.33427" strokeLinecap="round"><path d="M233.727 90.3884C233.996 89.9752 234.242 89.5523 234.475 89.1253C237.352 83.8549 243.924 77.2409 247.624 81.9704V81.9704C249.198 83.9838 250.552 86.2015 253.521 88.048C258.165 90.9364 264.547 90.0243 268.458 86.506C271.882 83.4263 274.155 79.5994 278.231 77.1084C279.927 76.0719 282.283 74.8904 284.355 75.7296C286.438 76.5729 287.036 78.9452 287.715 80.7368C288.694 83.3183 291.025 90.513 295.51 88.7556C297.904 87.8177 299.931 85.0168 301.539 83.2767C303.048 81.6435 304.487 79.9696 305.955 78.3057" /><path d="M233.727 90.3884C237.282 79.4693 246.18 83.5392 253.521 91.9345C255.594 94.3052 257.896 95.3749 260.158 95.2813C267.436 94.9804 269.253 85.2428 271.628 78.357C273.229 73.7143 275.014 69.8123 277.435 66.853C279.131 64.7799 281.487 62.417 283.559 64.0954C285.642 65.782 286.24 70.5266 286.919 74.1098C287.898 79.2728 290.884 97.639 295.37 94.1242C297.764 92.2484 299.931 85.8721 301.539 82.3918C303.048 79.1254 303.242 78.7726 304.71 75.4448" strokeDasharray="0.13 2.67" /></g>;
    $[95] = t61;
  } else {
    t61 = $[95];
  }
  let t62;
  if ($[96] !== t54 || $[97] !== t60) {
    t62 = <g transform="translate(-2 2)" opacity={t54} style={_styles.transitions}>{t60}{t61}</g>;
    $[96] = t54;
    $[97] = t60;
    $[98] = t62;
  } else {
    t62 = $[98];
  }
  let t63;
  if ($[99] !== t50 || $[100] !== t51 || $[101] !== t53 || $[102] !== t62) {
    t63 = <g style={_styles.transitions} transform={t50} opacity={t51}>{t53}{t62}</g>;
    $[99] = t50;
    $[100] = t51;
    $[101] = t53;
    $[102] = t62;
    $[103] = t63;
  } else {
    t63 = $[103];
  }
  let t64;
  if ($[104] !== t46 || $[105] !== t47 || $[106] !== t49 || $[107] !== t63) {
    t64 = <g>{t46}{t47}{t49}{t63}</g>;
    $[104] = t46;
    $[105] = t47;
    $[106] = t49;
    $[107] = t63;
    $[108] = t64;
  } else {
    t64 = $[108];
  }
  const t65 = selectedPart != 2 ? _styles.opacities.full : _styles.opacities.hidden;
  let t66;
  if ($[109] !== textFillColor) {
    t66 = <path d="M2.77322 368.734L6.28322 376.853H6.38722L9.89722 368.734H11.3857V378.094H10.2807V370.645H10.1767L6.95922 378.094H5.68522L2.46772 370.645H2.36372V378.094H1.25872V368.734H2.77322ZM13.3147 373.31C13.358 372.903 13.501 372.55 13.7437 372.251C13.9907 371.948 14.3244 371.716 14.7447 371.555C15.165 371.395 15.6569 371.315 16.2202 371.315C17.0912 371.315 17.7585 371.516 18.2222 371.919C18.6902 372.318 18.9242 372.923 18.9242 373.733V378.094H17.8582V377.184H17.7542C17.5939 377.483 17.3209 377.735 16.9352 377.938C16.5539 378.138 16.0512 378.237 15.4272 378.237C14.9505 378.237 14.528 378.159 14.1597 378.003C13.7914 377.847 13.5054 377.622 13.3017 377.327C13.098 377.033 12.9962 376.684 12.9962 376.281C12.9962 375.661 13.2042 375.187 13.6202 374.857C14.0362 374.524 14.606 374.311 15.3297 374.22L17.8192 373.908V373.668C17.8192 373.161 17.6849 372.795 17.4162 372.569C17.1475 372.34 16.7402 372.225 16.1942 372.225C15.6482 372.225 15.2214 372.331 14.9137 372.543C14.606 372.756 14.4305 373.076 14.3872 373.505H13.3147V373.31ZM14.1337 376.229C14.1337 376.571 14.2659 376.838 14.5302 377.028C14.7989 377.219 15.1607 377.314 15.6157 377.314C16.0317 377.314 16.4044 377.239 16.7337 377.087C17.0674 376.931 17.3317 376.695 17.5267 376.378C17.7217 376.062 17.8192 375.67 17.8192 375.202V374.792L15.5572 375.091C15.0979 375.152 14.7447 375.273 14.4977 375.455C14.255 375.637 14.1337 375.895 14.1337 376.229ZM21.861 371.471V372.264H21.965C22.0906 372.004 22.2791 371.802 22.5305 371.659C22.7818 371.516 23.1393 371.445 23.603 371.445H24.526V372.433H23.629C23.018 372.433 22.576 372.6 22.303 372.933C22.0343 373.263 21.9 373.772 21.9 374.461V378.094H20.756V371.471H21.861ZM26.94 368.734V374.155H27.044L30.3005 371.471H31.6135V371.666L27.9735 374.63L31.698 377.899V378.094H30.3655L27.044 375.15H26.94V378.094H25.796V368.734H26.94ZM32.1915 374.779C32.1915 374.043 32.3215 373.414 32.5815 372.894C32.8415 372.374 33.212 371.982 33.693 371.718C34.174 371.449 34.7438 371.315 35.4025 371.315C36.0525 371.315 36.605 371.445 37.06 371.705C37.5193 371.961 37.866 372.327 38.1 372.803C38.3383 373.28 38.4575 373.843 38.4575 374.493V375.065H33.2965C33.3052 375.525 33.394 375.926 33.563 376.268C33.7363 376.606 33.979 376.868 34.291 377.054C34.6073 377.236 34.9778 377.327 35.4025 377.327C35.7882 377.327 36.1153 377.265 36.384 377.139C36.657 377.009 36.8672 376.846 37.0145 376.651C37.1618 376.452 37.255 376.242 37.294 376.021H38.321V376.216C38.2647 376.528 38.1282 376.84 37.9115 377.152C37.6992 377.46 37.3828 377.72 36.9625 377.932C36.5465 378.144 36.0308 378.25 35.4155 378.25C34.7568 378.25 34.1848 378.118 33.6995 377.854C33.2185 377.585 32.8458 377.191 32.5815 376.671C32.3215 376.151 32.1915 375.52 32.1915 374.779ZM37.3655 374.162C37.3612 373.781 37.2832 373.445 37.1315 373.154C36.9798 372.864 36.7567 372.639 36.462 372.478C36.1717 372.314 35.8163 372.231 35.396 372.231C34.9843 372.231 34.6268 372.314 34.3235 372.478C34.0245 372.643 33.7883 372.871 33.615 373.161C33.446 373.451 33.3463 373.785 33.316 374.162H37.3655ZM40.6124 371.471V369.651H41.7564V371.471H43.7389V372.4H41.7564V377.002L41.8604 377.106H43.7194V378.094H42.3154C41.765 378.094 41.3425 377.958 41.0479 377.685C40.7575 377.412 40.6124 377.009 40.6124 376.476V372.4H39.0459V371.471H40.6124Z" fill={textFillColor} />;
    $[109] = textFillColor;
    $[110] = t66;
  } else {
    t66 = $[110];
  }
  let t67;
  if ($[111] !== pathsFillColor) {
    t67 = <path d="M166 373L54 373" stroke={pathsFillColor} strokeWidth={_styles.labels.dashWidth} />;
    $[111] = pathsFillColor;
    $[112] = t67;
  } else {
    t67 = $[112];
  }
  let t68;
  if ($[113] !== textFillColor) {
    t68 = <path d="M156 415.632L157.346 420.949H157.456L159.068 415.632H160.336L161.954 420.949H162.058L163.397 415.632H164.457V415.827L162.721 422.255H161.35L159.738 416.958H159.634L158.022 422.255H156.644L154.908 415.827V415.632H156ZM165.34 417.367C165.34 416.977 165.446 416.641 165.658 416.36C165.875 416.074 166.185 415.857 166.588 415.71C166.991 415.558 167.474 415.482 168.037 415.482C168.891 415.482 169.565 415.653 170.059 415.996C170.557 416.338 170.832 416.823 170.884 417.452V417.647H169.838C169.795 416.806 169.197 416.386 168.044 416.386C167.528 416.386 167.13 416.468 166.848 416.633C166.571 416.797 166.432 417.033 166.432 417.341C166.432 417.631 166.536 417.85 166.744 417.998C166.952 418.141 167.247 418.236 167.628 418.284L168.765 418.427C169.463 418.513 170.009 418.715 170.403 419.031C170.798 419.343 170.995 419.787 170.995 420.364C170.995 420.775 170.889 421.135 170.676 421.443C170.468 421.746 170.156 421.982 169.74 422.151C169.324 422.32 168.82 422.405 168.226 422.405C167.628 422.405 167.104 422.322 166.653 422.158C166.207 421.989 165.858 421.752 165.606 421.449C165.355 421.141 165.216 420.788 165.19 420.39V420.195H166.25C166.28 420.628 166.473 420.955 166.828 421.176C167.184 421.393 167.65 421.501 168.226 421.501C168.772 421.501 169.186 421.406 169.467 421.215C169.749 421.024 169.89 420.76 169.89 420.422C169.89 420.11 169.773 419.872 169.539 419.707C169.309 419.542 168.969 419.432 168.518 419.376L167.381 419.233C166.02 419.059 165.34 418.437 165.34 417.367ZM173.219 415.632V413.812H174.363V415.632H176.346V416.561H174.363V421.163L174.467 421.267H176.326V422.255H174.922C174.372 422.255 173.949 422.119 173.655 421.846C173.364 421.573 173.219 421.17 173.219 420.637V416.561H171.653V415.632H173.219ZM184.355 412.895V413.903H179.025V417.023H183.705V418.03H179.025V421.248H184.446V422.255H177.842V412.895H184.355ZM193.24 412.895V413.929H189.86V422.255H188.664V413.929H185.284V412.895H193.24ZM195.828 412.895V417.029H200.846V412.895H202.042V422.255H200.846V418.082H195.828V422.255H194.638V412.895H195.828ZM211.331 412.895V413.09L207.665 422.255H206.56V422.06L210.226 412.895H211.331ZM216.977 412.895V418.778C216.977 419.618 217.196 420.26 217.633 420.702C218.071 421.144 218.714 421.365 219.564 421.365C220.413 421.365 221.057 421.144 221.494 420.702C221.932 420.26 222.151 419.618 222.151 418.778V412.895H223.347V418.804C223.347 419.545 223.206 420.186 222.924 420.728C222.643 421.265 222.22 421.681 221.657 421.976C221.093 422.266 220.396 422.411 219.564 422.411C218.74 422.411 218.047 422.266 217.484 421.976C216.92 421.681 216.496 421.263 216.21 420.721C215.928 420.179 215.787 419.54 215.787 418.804V412.895H216.977ZM225.145 415.268C225.145 414.752 225.279 414.306 225.548 413.929C225.821 413.547 226.215 413.255 226.731 413.051C227.251 412.847 227.875 412.746 228.603 412.746C229.335 412.746 229.972 412.854 230.514 413.071C231.056 413.287 231.478 413.606 231.782 414.026C232.085 414.442 232.254 414.945 232.289 415.534V415.729H231.164C231.134 415.04 230.889 414.533 230.43 414.208C229.97 413.879 229.364 413.714 228.61 413.714C227.899 413.714 227.338 413.851 226.926 414.124C226.514 414.392 226.309 414.771 226.309 415.261C226.309 415.738 226.484 416.097 226.835 416.34C227.186 416.578 227.704 416.743 228.389 416.834L229.383 416.971C230.358 417.101 231.101 417.378 231.613 417.803C232.124 418.223 232.38 418.838 232.38 419.649C232.38 420.221 232.239 420.712 231.957 421.124C231.68 421.536 231.275 421.852 230.742 422.073C230.213 422.294 229.578 422.405 228.837 422.405C228.087 422.405 227.424 422.294 226.848 422.073C226.272 421.848 225.815 421.521 225.477 421.092C225.139 420.658 224.948 420.143 224.905 419.545V419.35H226.023C226.092 420.065 226.38 420.591 226.887 420.929C227.394 421.267 228.04 421.436 228.824 421.436C229.561 421.436 230.144 421.289 230.573 420.994C231.006 420.695 231.223 420.264 231.223 419.701C231.223 419.176 231.036 418.784 230.664 418.524C230.291 418.26 229.754 418.082 229.052 417.991L228.057 417.855C227.099 417.729 226.374 417.458 225.88 417.042C225.39 416.622 225.145 416.03 225.145 415.268ZM237.438 412.895C238.353 412.895 239.156 413.084 239.85 413.461C240.543 413.838 241.078 414.379 241.455 415.086C241.837 415.788 242.027 416.617 242.027 417.575C242.027 418.533 241.837 419.365 241.455 420.071C241.078 420.773 240.543 421.313 239.85 421.69C239.156 422.067 238.353 422.255 237.438 422.255H234.006V412.895H237.438ZM237.406 421.228C238.09 421.228 238.688 421.087 239.2 420.806C239.715 420.52 240.114 420.104 240.396 419.558C240.677 419.007 240.818 418.346 240.818 417.575C240.818 416.804 240.677 416.145 240.396 415.599C240.114 415.049 239.715 414.633 239.2 414.351C238.688 414.065 238.09 413.922 237.406 413.922H235.196V421.228H237.406ZM243.186 417.575C243.186 416.548 243.373 415.671 243.745 414.943C244.118 414.215 244.649 413.662 245.338 413.285C246.031 412.904 246.846 412.713 247.782 412.713C248.653 412.713 249.39 412.869 249.992 413.181C250.599 413.489 251.056 413.881 251.363 414.358C251.671 414.83 251.842 415.313 251.877 415.807V416.002H250.765C250.735 415.647 250.614 415.298 250.401 414.956C250.193 414.609 249.873 414.321 249.439 414.091C249.006 413.857 248.46 413.74 247.801 413.74C247.091 413.74 246.48 413.89 245.968 414.189C245.461 414.488 245.071 414.925 244.798 415.502C244.53 416.074 244.395 416.765 244.395 417.575C244.395 418.385 244.53 419.079 244.798 419.655C245.071 420.227 245.461 420.663 245.968 420.962C246.48 421.261 247.091 421.41 247.801 421.41C248.46 421.41 249.006 421.293 249.439 421.059C249.877 420.821 250.202 420.526 250.414 420.175C250.627 419.824 250.752 419.46 250.791 419.083H251.903V419.278C251.86 419.785 251.682 420.279 251.37 420.76C251.062 421.241 250.605 421.642 249.998 421.963C249.396 422.279 248.657 422.437 247.782 422.437C246.846 422.437 246.031 422.249 245.338 421.872C244.649 421.49 244.118 420.936 243.745 420.208C243.373 419.48 243.186 418.602 243.186 417.575Z" fill={textFillColor} />;
    $[113] = textFillColor;
    $[114] = t68;
  } else {
    t68 = $[114];
  }
  let t69;
  if ($[115] !== t65 || $[116] !== t66 || $[117] !== t67 || $[118] !== t68) {
    t69 = <g opacity={t65}>{t66}{t67}{t68}</g>;
    $[115] = t65;
    $[116] = t66;
    $[117] = t67;
    $[118] = t68;
    $[119] = t69;
  } else {
    t69 = $[119];
  }
  let t70;
  let t71;
  let t72;
  let t73;
  let t74;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <path d="M9.74156 81.5309C9.99444 89.1094 15.2546 95.4378 21.228 99.6162C30.6014 106.157 42.1673 109.185 53.4446 110.167C63.0748 110.95 72.8589 110.227 82.2109 107.756C95.3466 104.307 111.925 95.4171 112.14 80.6632L112.16 91.3931C112.352 99.1827 106.327 107.56 96.1295 113.027C88.2586 117.324 79.4308 119.645 70.5561 120.658C55.6176 122.295 38.7634 120.298 25.4693 112.989C19.1249 109.45 13.0742 104.342 10.6781 97.275C10.261 96.0081 9.96806 94.6976 9.83875 93.3701C9.62801 92.1083 9.79142 82.3117 9.72725 80.9557C9.72781 81.1474 9.73305 81.339 9.74106 81.5295L9.74156 81.5309Z" />;
    t71 = <path d="M9.74156 70.8038C9.99444 78.3823 15.2546 84.7107 21.228 88.8891C30.6014 95.4301 42.1673 98.4584 53.4446 99.4402C63.0748 100.223 72.8589 99.5003 82.2109 97.0291C95.3466 93.5802 111.925 84.6901 112.14 69.9361L112.16 80.6661C112.352 88.4557 106.327 96.8326 96.1295 102.3C88.2586 106.597 79.4308 108.918 70.5561 109.931C55.6176 111.568 38.7634 109.571 25.4693 102.262C19.1249 98.7233 13.0742 93.6152 10.6781 86.5479C10.261 85.2811 9.96806 83.9706 9.83875 82.643C9.62801 81.3813 9.79142 71.5847 9.72725 70.2287C9.72781 70.4204 9.73305 70.6119 9.74106 70.8025L9.74156 70.8038Z" />;
    t72 = <path d="M9.74156 60.0875C9.99444 67.666 15.2546 73.9944 21.228 78.1728C30.6014 84.7138 42.1673 87.7421 53.4446 88.7239C63.0748 89.5062 72.8589 88.784 82.2109 86.3128C95.3466 82.8639 111.925 73.9738 112.14 59.2198L112.16 69.9498C112.352 77.7394 106.327 86.1163 96.1295 91.5832C88.2586 95.8808 79.4308 98.2021 70.5561 99.2146C55.6176 100.852 38.7634 98.855 25.4693 91.5454C19.1249 88.007 13.0742 82.8989 10.6781 75.8316C10.261 74.5647 9.96806 73.2543 9.83875 71.9267C9.62801 70.665 9.79142 60.8684 9.72725 59.5124C9.72781 59.7041 9.73305 59.8956 9.74106 60.0862L9.74156 60.0875Z" />;
    t73 = <path d="M24.8447 27.6773C4.78296 39.2603 4.67751 58.0401 24.6087 69.623C44.5398 81.206 76.9613 81.2061 97.0236 69.6245C117.087 58.041 117.192 39.2613 97.2609 27.6783C77.3279 16.0945 44.9064 16.0943 24.8447 27.6773Z" />;
    t74 = <path d="M9.74156 49.371C9.99444 56.9494 15.2546 63.2779 21.228 67.4562C30.6014 73.9972 42.1673 77.0255 53.4446 78.0074C63.0748 78.7897 72.8589 78.0674 82.2109 75.5962C95.3466 72.1474 111.925 63.2572 112.14 48.5033L112.16 59.2332C112.352 67.0228 106.327 75.3997 96.1295 80.8666C88.2586 85.1643 79.4308 87.4855 70.5561 88.498C55.6176 90.1354 38.7634 88.1385 25.4693 80.8289C19.1249 77.2904 13.0742 72.1824 10.6781 65.115C10.261 63.8482 9.96806 62.5377 9.83875 61.2102C9.62801 59.9484 9.79142 50.1518 9.72725 48.7958C9.72781 48.9875 9.73305 49.1791 9.74106 49.3696L9.74156 49.371Z" />;
    $[120] = t70;
    $[121] = t71;
    $[122] = t72;
    $[123] = t73;
    $[124] = t74;
  } else {
    t70 = $[120];
    t71 = $[121];
    t72 = $[122];
    t73 = $[123];
    t74 = $[124];
  }
  let t75;
  if ($[125] !== backgroundFillColor) {
    t75 = <g fill={backgroundFillColor} stroke={_styles.colors.half} strokeMiterlimit="10" transform="translate(148 270) scale(0.8)">{t70}{t71}{t72}{t73}{t74}</g>;
    $[125] = backgroundFillColor;
    $[126] = t75;
  } else {
    t75 = $[126];
  }
  const t76 = `${base}/coinstack.png`;
  const t77 = selectedPart != 2 ? 1 : 0;
  let t78;
  if ($[127] !== t76 || $[128] !== t77) {
    t78 = <image width="99.6" height="112" x="146.9" y="269.8" href={t76} opacity={t77} style={_styles.transitions} />;
    $[127] = t76;
    $[128] = t77;
    $[129] = t78;
  } else {
    t78 = $[129];
  }
  let t79;
  if ($[130] !== t75 || $[131] !== t78) {
    t79 = <g transform="scale(1.1) translate(-11 -5)">{t75}{t78}</g>;
    $[130] = t75;
    $[131] = t78;
    $[132] = t79;
  } else {
    t79 = $[132];
  }
  let t80;
  if ($[133] !== t69 || $[134] !== t79) {
    t80 = <g>{t69}{t79}</g>;
    $[133] = t69;
    $[134] = t79;
    $[135] = t80;
  } else {
    t80 = $[135];
  }
  let t81;
  if ($[136] !== className || $[137] !== ref || $[138] !== style || $[139] !== t18 || $[140] !== t45 || $[141] !== t64 || $[142] !== t80) {
    t81 = <svg viewBox="0 0 383 449" fill="none" className={className} ref={ref} style={style}>{t18}{t45}{t64}{t80}</svg>;
    $[136] = className;
    $[137] = ref;
    $[138] = style;
    $[139] = t18;
    $[140] = t45;
    $[141] = t64;
    $[142] = t80;
    $[143] = t81;
  } else {
    t81 = $[143];
  }
  return t81;
}
export default memo(forwardRef(HowItWorksBorrowGraphics));